import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  EventBusy,
  Schedule,
  LocationOn,
  Videocam,
  Phone,
  Notes,
  Add,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  useBooking,
  useUpdateBookingStatus,
  useAddBookingNote,
} from '../../hooks/useBookings';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { COLORS } from '@elevair/shared';

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  scheduled: { label: 'Scheduled', color: '#2563EB', bgColor: '#DBEAFE' },
  completed: { label: 'Completed', color: '#059669', bgColor: '#D1FAE5' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bgColor: '#FEE2E2' },
  no_show: { label: 'No Show', color: '#D97706', bgColor: '#FEF3C7' },
  rescheduled: { label: 'Rescheduled', color: '#6B7280', bgColor: '#F3F4F6' },
};

const meetingTypeLabels: Record<string, { label: string; icon: React.ReactNode }> = {
  online: { label: 'Online Meeting', icon: <Videocam fontSize="small" /> },
  in_person: { label: 'In Person', icon: <LocationOn fontSize="small" /> },
  phone: { label: 'Phone Call', icon: <Phone fontSize="small" /> },
  flexible: { label: 'Flexible', icon: <Schedule fontSize="small" /> },
};

const cancellationReasonLabels: Record<string, string> = {
  client_request: 'Client requested cancellation',
  provider_unavailable: 'Provider unavailable',
  emergency: 'Emergency',
  duplicate: 'Duplicate booking',
  other: 'Other',
};

const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export default function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: booking, isLoading, error } = useBooking(id || '');
  const updateStatus = useUpdateBookingStatus();
  const addNote = useAddBookingNote();

  const handleMarkComplete = async () => {
    try {
      await updateStatus.mutateAsync({ id: id!, status: 'completed' });
      setCompleteDialogOpen(false);
    } catch {
      setActionError('Failed to mark booking as complete');
    }
  };

  const handleMarkNoShow = async () => {
    try {
      await updateStatus.mutateAsync({ id: id!, status: 'no_show' });
      setNoShowDialogOpen(false);
    } catch {
      setActionError('Failed to mark booking as no-show');
    }
  };

  const handleCancel = async () => {
    try {
      await updateStatus.mutateAsync({
        id: id!,
        status: 'cancelled',
        cancellationReason: cancelReason || undefined,
        cancellationNote: cancelNote || undefined,
      });
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelNote('');
    } catch {
      setActionError('Failed to cancel booking');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    try {
      await addNote.mutateAsync({ bookingId: id!, content: noteContent.trim() });
      setNoteContent('');
    } catch {
      setActionError('Failed to add note');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/bookings')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight={600}>Booking</Typography>
        </Box>
        <Alert severity="error">
          {error ? 'Failed to load booking details' : 'Booking not found'}
        </Alert>
      </Box>
    );
  }

  const status = statusConfig[booking.status] ?? { label: booking.status, color: '#6B7280', bgColor: '#F3F4F6' };
  const isScheduled = booking.status === 'scheduled';
  const meeting = meetingTypeLabels[booking.meetingType] ?? { label: booking.meetingType, icon: <Schedule fontSize="small" /> };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <IconButton onClick={() => navigate('/bookings')}>
          <ArrowBack />
        </IconButton>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h4" fontWeight={600}>
              {booking.serviceName}
            </Typography>
            <Chip
              label={status.label}
              sx={{
                backgroundColor: status.bgColor,
                color: status.color,
                fontWeight: 600,
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {booking.formattedDate} &bull; {booking.formattedTimeRange}
          </Typography>
        </Box>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Client & Service Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Client</Typography>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    bgcolor: booking.clientId?.avatarColor || COLORS.primary,
                    width: 48,
                    height: 48,
                  }}
                >
                  {booking.clientName?.charAt(0)?.toUpperCase() || 'C'}
                </Avatar>
                <Box>
                  <Typography fontWeight={600}>{booking.clientName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {booking.clientEmail}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Service</Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: booking.serviceColor || COLORS.primary,
                  }}
                />
                <Typography fontWeight={600}>{booking.serviceName}</Typography>
              </Box>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(booking.servicePrice)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {booking.duration} minutes
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Schedule & Meeting Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Schedule</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Date</Typography>
                  <Typography fontWeight={500}>{booking.formattedDate}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Time</Typography>
                  <Typography fontWeight={500}>{booking.formattedTimeRange}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Duration</Typography>
                  <Typography fontWeight={500}>{booking.duration} minutes</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Timezone</Typography>
                  <Typography fontWeight={500}>{booking.timezone}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Meeting Details</Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {meeting.icon}
                <Typography fontWeight={500}>{meeting.label}</Typography>
              </Box>
              {booking.meetingLocation && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                  {booking.meetingLocation}
                </Typography>
              )}
              {booking.meetingUrl && (
                <Typography variant="body2" sx={{ ml: 3.5 }}>
                  <a href={booking.meetingUrl} target="_blank" rel="noopener noreferrer">
                    {booking.meetingUrl}
                  </a>
                </Typography>
              )}
              {booking.meetingInstructions && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 3.5 }}>
                  {booking.meetingInstructions}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Notes Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Notes</Typography>

              {booking.internalNotes && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <Notes fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Internal Notes
                  </Typography>
                  <Typography variant="body2">{booking.internalNotes}</Typography>
                </Box>
              )}

              {booking.notes?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {booking.notes.map((note) => (
                    <Box key={note._id} sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'divider' }}>
                      <Typography variant="body2">{note.content}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {note.createdBy?.profile?.firstName} {note.createdBy?.profile?.lastName}
                        {' — '}
                        {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Box display="flex" gap={1}>
                <TextField
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Add a note..."
                  size="small"
                  fullWidth
                  multiline
                  maxRows={3}
                  inputProps={{ maxLength: 2000 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!noteContent.trim() || addNote.isPending}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  <Add />
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Actions Card */}
          {isScheduled && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actions</Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => setCompleteDialogOpen(true)}
                    fullWidth
                  >
                    Mark Complete
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<EventBusy />}
                    onClick={() => setNoShowDialogOpen(true)}
                    fullWidth
                  >
                    Mark No-Show
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => setCancelDialogOpen(true)}
                    fullWidth
                  >
                    Cancel Booking
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Cancellation Info */}
          {booking.status === 'cancelled' && (
            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error">
                  Cancellation Details
                </Typography>
                {booking.cancellationReason && (
                  <Box mb={1}>
                    <Typography variant="body2" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">
                      {cancellationReasonLabels[booking.cancellationReason] || booking.cancellationReason}
                    </Typography>
                  </Box>
                )}
                {booking.cancellationNote && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Note</Typography>
                    <Typography variant="body2">{booking.cancellationNote}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Details Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Details</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={status.label}
                    size="small"
                    sx={{
                      backgroundColor: status.bgColor,
                      color: status.color,
                      fontWeight: 600,
                      mt: 0.5,
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Pricing</Typography>
                  <Typography variant="body2">{booking.servicePricingType || 'Fixed'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="body2">
                    {format(new Date(booking.createdAt), 'MMM d, yyyy h:mm a')}
                  </Typography>
                </Box>
                {booking.updatedAt !== booking.createdAt && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body2">
                      {format(new Date(booking.updatedAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mark Complete Dialog */}
      <ConfirmDialog
        open={completeDialogOpen}
        title="Mark as Complete"
        message="Are you sure you want to mark this booking as complete?"
        confirmText="Complete"
        variant="info"
        confirmColor="success"
        onConfirm={handleMarkComplete}
        onCancel={() => setCompleteDialogOpen(false)}
      />

      {/* No-Show Dialog */}
      <ConfirmDialog
        open={noShowDialogOpen}
        title="Mark as No-Show"
        message="Mark this booking as a no-show? The client did not attend the scheduled appointment."
        confirmText="Mark No-Show"
        variant="warning"
        onConfirm={handleMarkNoShow}
        onCancel={() => setNoShowDialogOpen(false)}
      />

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Cancellation Reason</InputLabel>
            <Select
              value={cancelReason}
              label="Cancellation Reason"
              onChange={(e) => setCancelReason(e.target.value)}
            >
              <MenuItem value="client_request">Client requested</MenuItem>
              <MenuItem value="provider_unavailable">Provider unavailable</MenuItem>
              <MenuItem value="emergency">Emergency</MenuItem>
              <MenuItem value="duplicate">Duplicate booking</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            value={cancelNote}
            onChange={(e) => setCancelNote(e.target.value)}
            label="Additional Notes (optional)"
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)} color="inherit">
            Keep Booking
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={updateStatus.isPending}
          >
            {updateStatus.isPending ? <CircularProgress size={20} color="inherit" /> : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
